import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {SplitPageLayout} from '@primer/react'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useHideFooter} from '@github-ui/use-hide-footer'
import {PullRequestFilesToolbar} from '../components/toolbar/PullRequestFilesToolbar'
import {PullRequestDiffsList} from '../components/PullRequestDiffsList'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import type {DiffEntry} from '@github-ui/diff-lines'
import {useTreePane} from '@github-ui/commits/shared/useTreePane'
import {
  useDiffViewSettingsData,
  useSplitViewPreference,
  useWhitespacePreference,
} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {useEffect, useMemo, useState} from 'react'
import type {DiffUser} from '@github-ui/diff-lines/types'
import {pullRequestContextLinesPath} from '@github-ui/paths'
import styles from './Files.module.css'
import {clsx} from 'clsx'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {SlottedAppLayout} from '../AppLayout'
import {pullRequestMarkersKey} from '../page-data/loaders/use-markers-data'
import {useQueryClient} from '@github-ui/react-query'
import type {CommentingImplementation} from '@github-ui/conversations'
import {usePendingReviewPageData} from '../page-data/payloads/pending-review'
import {SelectedRefContextProvider} from '../contexts/SelectedRefContext'
import type {FileFilterState} from '../components/diff-filtering/FileFilter'
import {PR_FILE_TREE_ID, PullRequestFileTree} from '../components/file-tree/PullRequestFileTree'
import {useFileFiltering} from '../hooks/use-file-filtering'
import {useCodeowners} from '../page-data/loaders/use-codeowners-data'
import {useDiffSummaries} from '../page-data/payloads/diff-summaries'
import type {Codeowners} from '../page-data/payloads/codeowners'

export function FilesEntrypoint() {
  return (
    <ErrorBoundary critical fallback={<PullRequestErrorState text="Changes cannot be loaded" />}>
      <Files />
    </ErrorBoundary>
  )
}

export function Files() {
  const data = useRoutePayload<FilesRoutePayload>()

  return <FilesComponent {...data} />
}

export function FilesComponent(payload: FilesRoutePayload) {
  const {
    annotations,
    codeowners,
    commits,
    diffSummaries: initialDiffSummaries,
    diffContents: diffEntries,
    fileFilter,
    pullRequest,
    repository,
    threadPreviews,
    markers: initialMarkers,
    user,
    viewerPendingReview,
  } = payload

  const {data: pendingReviewPageData} = usePendingReviewPageData({
    pathName: pullRequest.pathName,
    initialData: viewerPendingReview,
  })

  const commentBatchPending = useMemo(() => {
    return (pendingReviewPageData?.comments.length ?? 0) > 0
  }, [pendingReviewPageData])

  useHideFooter(true)

  /*
   * Viewer settings
   */
  const viewSettingsData = useViewerSettings(user)

  const commentBoxConfig: CommentingImplementation['commentBoxConfig'] = useMemo(() => {
    return {
      emojiSkinTonePreference: user.commentingSettings.emojiSkinTonePreference,
      pasteUrlsAsPlainText: user.commentingSettings.pasteUrlsAsPlainText,
      useMonospaceFont: user.commentingSettings.useMonospaceFont,
    }
  }, [
    user.commentingSettings.emojiSkinTonePreference,
    user.commentingSettings.pasteUrlsAsPlainText,
    user.commentingSettings.useMonospaceFont,
  ])

  const {data: settingsData} = useDiffViewSettingsData(viewSettingsData)

  const currentUser: DiffUser = useMemo(() => {
    return {
      avatarURL: user.currentUserAvatarUrl || '',
      login: user.currentUserLogin || '',
      tabSize: undefined, //TODO, do we even support this setting in diffs?
      splitPreference: settingsData?.splitPreference ?? 'unified',
      lineSpacing: settingsData?.lineSpacing ?? 'relaxed',
      canComment: user.canComment,
      commentsPreference: settingsData?.commentsPreference ?? 'visible',
      hasCopilotAccess: user.hasCopilotAccess,
    }
  }, [
    settingsData?.commentsPreference,
    settingsData?.lineSpacing,
    settingsData?.splitPreference,
    user.canComment,
    user.currentUserAvatarUrl,
    user.currentUserLogin,
    user.hasCopilotAccess,
  ])

  // eventually we're going to use the treeToggleElement and collapseTree returned from this hook too
  const {splitPagePaneHidden, splitPageContentHidden, isTreeExpanded, isMobileTreeExpanded, treeToggleElement} =
    useTreePane(PR_FILE_TREE_ID, user.isFileTreeExpanded, {
      size: 'small',
      mobileBreakpoint: 'md', // this will eventually be dynamic based on diff view setting
    })

  /*
   * Diff and Filtering
   */

  // Codeowners data should be set in TSQ before filtering any diff summaries or diff entries,
  // because filtering is dependent on the codeowners data.
  const codeownersOptions: {basePath: string; initialData?: Codeowners} = {basePath: pullRequest.pathName}
  // Only set initialData if it exists
  if (codeowners) codeownersOptions.initialData = codeowners
  useCodeowners(codeownersOptions)

  const {data: diffSummaries} = useDiffSummaries(pullRequest.pathName, initialDiffSummaries)
  const initialFileFilterState: FileFilterState = {
    ...fileFilter.initialState,
    unselectedFileExtensions: new Set<string>(fileFilter.initialState.unselectedFileExtensions),
  }
  const [fileFilterState, setFileFilterState] = useState<FileFilterState>(initialFileFilterState)
  const [hiddenFilepathMap, filteredDiffSummaries] = useFileFiltering(
    diffSummaries,
    fileFilterState,
    pullRequest.pathName,
  )

  const filteredDiffEntries: DiffEntry[] = useMemo(() => {
    return diffEntries
      .filter(diff => hiddenFilepathMap.get(diff.path) !== true)
      .map(diff => {
        return {
          ...diff,
          helpUrl: '',
          collapsed: diff.reviewed,
          commentingEnabled: true,
          changeType: diff.status,
          // pullRequest, // TODO might need to do some mapping here
        }
      })
  }, [diffEntries, hiddenFilepathMap])

  const contextLinesURL = useMemo(() => {
    return pullRequestContextLinesPath({
      owner: repository.ownerLogin,
      repo: repository.name,
      number: pullRequest.number,
    })
  }, [repository.name, repository.ownerLogin, pullRequest.number])

  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.setQueryData(pullRequestMarkersKey(pullRequest.pathName), initialMarkers)
  }, [initialMarkers, pullRequest.pathName, queryClient])

  return (
    <SelectedRefContextProvider
      baseRefOid={pullRequest.comparison.baseOid}
      path={ssrSafeWindow?.location?.pathname ?? ''}
    >
      <SlottedAppLayout
        headerDivider="line"
        leadingContent={
          <PullRequestFilesToolbar
            annotations={annotations}
            baseRefOid="" // TODO - get this
            commits={commits}
            commentBoxConfig={commentBoxConfig}
            currentUserLogin={user.currentUserLogin}
            fileFilterMenuOptions={fileFilter.menuOptions}
            fileFilterState={fileFilterState}
            isFileTreeExpanded={isTreeExpanded}
            lastReviewOid={user.lastReviewOid}
            pullRequest={pullRequest}
            repository={repository}
            setFileFilterState={setFileFilterState}
            shouldShowViewedFilesCount={user.shouldShowViewedFilesCount}
            totalFilesCount={diffSummaries.length}
            threadPreviews={threadPreviews}
            treeToggleElement={treeToggleElement}
            viewedFilesCount={user.viewedFilesCount}
          />
        }
      >
        <SplitPageLayout.Pane
          position="start"
          sticky
          offsetHeader="72px" // sticky header height + a lil extra for spacing
          aria-label="File tree"
          padding="none"
          className={clsx(
            'height-full',
            splitPagePaneHidden ? styles.HiddenPane : styles.Pane,
            isMobileTreeExpanded && styles.MobileExpanded,
            isTreeExpanded && styles.TreeExpanded,
          )}
          divider={{regular: 'line', narrow: 'none'}}
          widthStorageKey="diff-tree-pane-width"
          resizable
        >
          <PullRequestFileTree
            diffs={diffSummaries}
            filteredDiffs={filteredDiffSummaries}
            fileFilterMenuOptions={fileFilter.menuOptions}
            fileFilterState={fileFilterState}
            ownerLogin={repository.ownerLogin}
            pathName={pullRequest.pathName}
            pullRequestId={pullRequest.globalRelayId}
            pullRequestNumber={pullRequest.number}
            repositoryName={repository.name}
            viewerLogin={currentUser.login}
            setFileFilterState={setFileFilterState}
          />
        </SplitPageLayout.Pane>
        <SplitPageLayout.Content
          as="div"
          width="full"
          hidden={splitPageContentHidden}
          padding="none"
          className={clsx(styles.Content, isTreeExpanded && styles.TreeExpanded)}
        >
          <PullRequestDiffsList
            commentBoxConfig={commentBoxConfig}
            contextLinesURL={contextLinesURL}
            currentUser={currentUser}
            headBranchName={pullRequest.headBranch}
            diffs={filteredDiffEntries}
            pullRequest={pullRequest}
            repository={repository}
            commentBatchPending={commentBatchPending}
          />
        </SplitPageLayout.Content>
      </SlottedAppLayout>
    </SelectedRefContextProvider>
  )
}

function useViewerSettings(user: FilesRoutePayload['user']) {
  const userSplitPreference = useSplitViewPreference(user.viewSettings.splitPreference)
  const userWhitespaceParam = useWhitespacePreference(user.viewSettings.hideWhitespace)
  return useMemo(() => {
    return {
      hideWhitespace: userWhitespaceParam,
      splitPreference: userSplitPreference,
      lineSpacing: user.viewSettings.lineSpacing,
      commentsPreference: user.viewSettings.commentsPreference,
    }
  }, [user.viewSettings.commentsPreference, user.viewSettings.lineSpacing, userSplitPreference, userWhitespaceParam])
}

try{ FilesEntrypoint.displayName ||= 'FilesEntrypoint' } catch {}
try{ Files.displayName ||= 'Files' } catch {}
try{ FilesComponent.displayName ||= 'FilesComponent' } catch {}